import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ALL_SERVICE_IDS, VEHICLE_SIZES, ALL_ADDONS, BRAND } from "../../lib/content";
import { submitEnquiry } from "../../lib/api";

const EMPTY = {
  full_name: "", phone: "", email: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "",
  vehicle_size: "", service_required: "", service_type: "",
  preferred_time: "", condition_notes: "", message: "",
};

export const BookingForm = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const est = location.state?.estimate;
    if (est?.pkg) {
      // best-effort: find matching service id
      const match = ALL_SERVICE_IDS.find((s) => s.name === est.pkg);
      if (match) setForm((f) => ({ ...f, service_required: match.id, vehicle_size: est.size?.toLowerCase() || "" }));
      if (est.addonIds?.length) setAddons(est.addonIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleAddon = (id) => setAddons((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const submit = async (enquiry_type) => {
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      const serviceObj = ALL_SERVICE_IDS.find((s) => s.id === form.service_required);
      const addonTotal = addons.reduce((s, id) => s + (ALL_ADDONS.find((a) => a.id === id)?.price || 0), 0);
      const sizeObj = VEHICLE_SIZES.find((s) => s.id === form.vehicle_size);
      const total = (serviceObj?.base || 0) + (sizeObj?.modifier || 0) + addonTotal;
      const payload = {
        ...form,
        service_required: serviceObj ? serviceObj.name : "",
        addons: addons.map((id) => ALL_ADDONS.find((a) => a.id === id)?.name).filter(Boolean),
        preferred_date: date ? format(date, "PPP") : "",
        estimated_total: total ? `$${total.toLocaleString()}` : "",
        enquiry_type,
      };
      const data = await submitEnquiry(payload);
      if (data.status === "success") {
        toast.success("Thank you for choosing SPECA Detailing. Your booking request has been received. Our team will contact you to confirm availability and final pricing.");
        setForm(EMPTY); setDate(null); setAddons([]);
      }
    } catch (err) {
      toast.error("Something went wrong. Please call us on " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {!compact && (
          <div className="lg:col-span-4">
            <span className="overline">Contact & Booking</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-5 leading-[1.05]">
              Your vehicle <span className="text-silver">deserves the best.</span>
            </h2>
            <p className="text-gray-400 font-light mt-5 leading-relaxed">
              Request a quote or book your vehicle in. Our team will contact you to confirm availability and final pricing.
            </p>
            <div className="mt-10 space-y-4">
              <a href={`tel:${BRAND.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 matte p-4 hover:border-silver/40 transition-colors">
                <Phone size={18} className="text-silver" />
                <span className="text-gray-200 font-mono-tech text-sm">{BRAND.phone}</span>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 matte p-4 hover:border-silver/40 transition-colors">
                <Mail size={18} className="text-silver" />
                <span className="text-gray-200 font-mono-tech text-sm break-all">{BRAND.email}</span>
              </a>
              <div className="flex items-center gap-4 matte p-4">
                <MapPin size={18} className="text-silver" />
                <span className="text-gray-200 font-mono-tech text-sm">{BRAND.location}</span>
              </div>
            </div>
          </div>
        )}

        <div className={compact ? "lg:col-span-12" : "lg:col-span-8"}>
          <Reveal>
            <div className="glass-card p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name" testid="form-full-name" value={form.full_name} onChange={set("full_name")} required />
                <Field label="Phone" testid="form-phone" value={form.phone} onChange={set("phone")} required />
                <Field label="Email" testid="form-email" type="email" value={form.email} onChange={set("email")} required />
                <Field label="Vehicle Make" testid="form-make" value={form.vehicle_make} onChange={set("vehicle_make")} />
                <Field label="Vehicle Model" testid="form-model" value={form.vehicle_model} onChange={set("vehicle_model")} />
                <Field label="Vehicle Year" testid="form-year" value={form.vehicle_year} onChange={set("vehicle_year")} />

                <SelectField label="Vehicle Size" testid="form-size" value={form.vehicle_size}
                  onValueChange={(v) => setForm((f) => ({ ...f, vehicle_size: v }))}
                  options={VEHICLE_SIZES.map((s) => ({ value: s.id, label: `${s.label} — ${s.description}` }))} />

                <SelectField label="Service Required" testid="form-service" value={form.service_required}
                  onValueChange={(v) => setForm((f) => ({ ...f, service_required: v }))}
                  options={ALL_SERVICE_IDS.map((s) => ({ value: s.id, label: `${s.name} — from $${s.base}` }))} />

                <SelectField label="Service Location" testid="form-service-type" value={form.service_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
                  options={[{ value: "mobile", label: "Mobile (at my location)" }, { value: "in-house", label: "In-house (SPECA studio)" }]} />

                <div className="flex flex-col gap-2">
                  <Label className="text-gray-300 text-xs uppercase tracking-widest">Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid="form-date" className="flex items-center gap-3 h-12 px-3 bg-[#0F0F0F] border border-white/15 text-left text-sm hover:border-white/30 transition-colors">
                        <CalendarIcon size={16} className="text-silver shrink-0" />
                        <span className={date ? "text-white" : "text-gray-500"}>{date ? format(date, "PPP") : "Pick a date"}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0F0F0F] border-white/15" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="text-white" />
                    </PopoverContent>
                  </Popover>
                </div>

                <Field label="Preferred Time" testid="form-time" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. 9am, afternoon" />
              </div>

              <div className="mt-6">
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Optional Add-Ons</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3 max-h-64 overflow-y-auto pr-1">
                  {ALL_ADDONS.map((a) => (
                    <label key={a.id} className={`matte px-3 py-2 cursor-pointer flex items-center gap-2 text-xs transition-colors ${addons.includes(a.id) ? "border-silver bg-white/5" : "hover:border-white/20"}`}>
                      <input type="checkbox" data-testid={`form-addon-${a.id}`} checked={addons.includes(a.id)} onChange={() => toggleAddon(a.id)} className="accent-silver" />
                      <span className="text-gray-200 flex-1">{a.name}</span>
                      <span className="font-mono-tech text-silver">{a.display}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-5">
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Vehicle Condition Notes</Label>
                <Textarea data-testid="form-condition" value={form.condition_notes} onChange={set("condition_notes")} placeholder="Current paint condition, heavy contamination, pet hair, interior stains, etc." className="bg-[#0F0F0F] border-white/15 text-white rounded-none min-h-[80px] focus-visible:ring-silver" />
              </div>

              <div className="flex flex-col gap-2 mt-5">
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Additional Message</Label>
                <Textarea data-testid="form-message" value={form.message} onChange={set("message")} placeholder="Anything else we should know" className="bg-[#0F0F0F] border-white/15 text-white rounded-none min-h-[80px] focus-visible:ring-silver" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button data-testid="submit-quote" disabled={loading} onClick={() => submit("quote")}
                  className="flex-1 flex items-center justify-center gap-2 bg-transparent text-white py-4 border border-white/20 hover:border-white hover:bg-white/5 transition-all uppercase tracking-widest text-sm font-bold disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null} Request a Quote
                </button>
                <button data-testid="submit-booking" disabled={loading} onClick={() => submit("booking")}
                  className="flex-1 flex items-center justify-center gap-2 bg-silver hover:bg-white text-black py-4 border border-silver transition-colors uppercase tracking-widest text-sm font-bold disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null} Book Now
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-4 text-center">Bookings are subject to confirmation. We&apos;ll contact you to confirm availability and final pricing.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, required, ...props }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-gray-300 text-xs uppercase tracking-widest">{label}{required && <span className="text-silver"> *</span>}</Label>
    <Input data-testid={testid} className="bg-[#0F0F0F] border-white/15 text-white h-12 rounded-none focus-visible:ring-silver placeholder:text-gray-600" {...props} />
  </div>
);

const SelectField = ({ label, testid, value, onValueChange, options }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-gray-300 text-xs uppercase tracking-widest">{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testid} className="bg-[#0F0F0F] border-white/15 text-white h-12 rounded-none focus:ring-silver">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent className="bg-[#0F0F0F] border-white/15 text-white max-h-72">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="focus:bg-white/10 focus:text-white">{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
